<template>
  <div class="gc-wo-bulk-update-audit-record">
    <div class="ard-layout">
      <!-- Main Column -->
      <div class="ard-main">
        <!-- Card 1: Transaction Details -->
        <div class="ard-card">
          <div class="ard-card-header">
            <span class="ard-card-title">Transaction details</span>
          </div>
          <div class="ard-card-body">
            <div class="ard-form-row">
              <div class="ard-form-field">
                <span class="ard-field-label">Transaction ID</span>
                <div class="ard-field-value ard-value-primary ard-mono">
                  {{ transactionId }}
                </div>
              </div>
              <div class="ard-form-field">
                <span class="ard-field-label">Status</span>
                <div class="ard-field-value">
                  <span :class="['ard-badge', statusClass]">{{
                    statusLabel
                  }}</span>
                </div>
              </div>
              <div class="ard-form-field">
                <span class="ard-field-label">Description</span>
                <div class="ard-field-value">{{ description || "—" }}</div>
              </div>
              <div class="ard-form-field">
                <span class="ard-field-label">Created by</span>
                <div class="ard-field-value">{{ createdByFormatted }}</div>
              </div>
            </div>
            <div class="ard-form-row">
              <div class="ard-form-field">
                <span class="ard-field-label">Committed by</span>
                <div class="ard-field-value">{{ committedByFormatted }}</div>
              </div>
              <div class="ard-form-field">
                <span class="ard-field-label">WOs updated</span>
                <div class="ard-field-value ard-value-primary">
                  {{ wosUpdatedCount }}/{{ wosIncludedCount }}
                </div>
              </div>
              <div class="ard-form-field"></div>
              <div class="ard-form-field"></div>
            </div>
          </div>
        </div>

        <!-- Card 2: Search Criteria Used -->
        <div class="ard-card">
          <div class="ard-card-header">
            <span class="ard-card-title">Search criteria used</span>
          </div>
          <div class="ard-card-body ard-prose-body">
            <template v-if="activeSearchTypes.length">
              <span class="ard-label">Search type(s):</span>
              {{ activeSearchTypes.join(", ") }}
              <span v-if="getSearchCodes" class="ard-separator">·</span>
              <span v-if="getSearchCodes" class="ard-label">Codes:</span>
              {{ getSearchCodes }}
            </template>
            <span v-else class="ard-label">No material criteria recorded</span>
            <span v-if="woStatusLabel" class="ard-separator">·</span>
            <span v-if="woStatusLabel" class="ard-label"
              >Work Order Status:</span
            >
            <span v-if="woStatusLabel">{{ woStatusLabel }}</span>
            <span v-if="woTypeLabel" class="ard-separator">·</span>
            <span v-if="woTypeLabel" class="ard-label">Work Order Type:</span>
            <span v-if="woTypeLabel">{{ woTypeLabel }}</span>
          </div>
        </div>

        <!-- Card 3: Update Configuration Applied -->
        <div v-if="materialUpdateConfig" class="ard-card">
          <div class="ard-card-header">
            <span class="ard-card-title">Update configuration applied</span>
          </div>
          <div class="ard-card-body ard-prose-body">
            <span class="ard-label">Material operation:</span>
            {{ formatOperation(materialUpdateConfig.Operation) }}
          </div>
        </div>

        <!-- Card: Diff preview -->
        <!-- <div v-if="hasDiff" class="ard-diff-preview">
          <GcWoBulkUpdateStepPreview
            :active="true"
            :matched-wo-list="matchedWoList"
            :excluded-wo-ids="excludedWoIds"
            :material-update-config="materialUpdateConfig"
            :diff-snapshot="diffSnapshot"
            :is-read-only="true"
          />
        </div> -->

        <!-- Card 4: WO Inclusion and Update Outcomes -->
        <div class="ard-card">
          <div class="ard-card-header">
            <span class="ard-card-title"
              >Work order inclusion and update outcomes</span
            >
          </div>
          <div class="ard-card-filters">
            <button
              v-for="tab in outcomeTabs"
              :key="tab.key"
              :class="[
                'ard-filter-tab',
                { 'is-active': activeOutcomeFilter === tab.key },
              ]"
              @click="activeOutcomeFilter = tab.key"
            >
              <span class="ard-tab-label">{{ tab.label }}</span>
              <span class="ard-tab-count">{{ tab.count }}</span>
            </button>
          </div>
          <div class="ard-dtable">
            <div class="ard-dtable-head">
              <div class="ard-dtable-hcell" style="width: 120px">WO ID</div>
              <div class="ard-dtable-hcell ard-col-product">Product</div>
              <div class="ard-dtable-hcell" style="width: 80px">Included</div>
              <div class="ard-dtable-hcell" style="width: 100px">Outcome</div>
              <div class="ard-dtable-hcell" style="flex: 1">Failure reason</div>
            </div>

            <div v-if="filteredRows.length > 0">
              <div
                v-for="(row, i) in filteredRows"
                :key="row.wono + '-' + i"
                :class="[
                  'ard-dtable-row',
                  { 'ard-row-failed': row.status === 'failed' },
                ]"
              >
                <div class="ard-dtable-cell" style="width: 120px">
                  {{ row.wono }}
                </div>
                <div class="ard-dtable-cell ard-col-product">
                  {{ row.ProductDescription }}
                </div>
                <div
                  class="ard-dtable-cell ard-text-center"
                  style="width: 80px"
                >
                  <span
                    :style="{
                      color: row.status === 'excluded' ? '#98A2B3' : '#057a3d',
                    }"
                  >
                    {{ row.status === "excluded" ? "–" : "✓" }}
                  </span>
                </div>
                <div class="ard-dtable-cell" style="width: 100px">
                  <span :class="['ard-badge', outcomeBadgeClass(row.status)]">
                    {{ outcomeLabel(row.status) }}
                  </span>
                </div>
                <div
                  class="ard-dtable-cell failure-reason"
                  style="flex: 1; color: #d92d20; font-size: 12px"
                >
                  <span
                    :class="[
                      { 'ard-reason-excluded': row.status === 'excluded' },
                    ]"
                    >{{ row.failureReason || "—" }}</span
                  >
                </div>
              </div>
            </div>
            <div v-else class="ard-dtable-empty">
              No work orders in this category
            </div>

            <div class="ard-dtable-foot">
              {{ filteredRows.length }} of {{ allOutcomeRows.length }} WOs
              shown · {{ successCount }} success · {{ failedCount }} failed ·
              {{ excludedCount }} excluded in this view
            </div>
          </div>
        </div>

        <!-- Card 5: Activity Log -->
        <div v-if="activityLog.length > 0" class="ard-card">
          <div class="ard-card-header">
            <span class="ard-card-title">Activity log</span>
          </div>
          <div class="ard-card-body ard-card-body-compact">
            <GcWoBulkUpdateActivityLog :activity-log="activityLog" />
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="ard-sidebar">
        <!-- Sidebar Card 1: Committed Summary -->
        <div class="ard-card">
          <div class="ard-card-header">
            <span class="ard-card-title">Committed summary</span>
          </div>
          <div class="ard-card-body">
            <div class="ard-sum-row">
              <span class="ard-sum-label">WOs updated</span>
              <span class="ard-sum-value ard-sv-blue"
                >{{ wosUpdatedCount }}/{{ wosIncludedCount }}</span
              >
            </div>
            <div class="ard-sum-row">
              <span class="ard-sum-label">WOs failed</span>
              <span
                :class="[
                  'ard-sum-value',
                  failedCount > 0 ? 'ard-sv-red' : 'ard-sv-muted',
                ]"
              >
                {{ failedCount }}
              </span>
            </div>
            <div class="ard-sum-row">
              <span class="ard-sum-label">WOs excluded</span>
              <span
                :class="[
                  'ard-sum-value',
                  excludedCount > 0 ? 'ard-sv-red' : 'ard-sv-muted',
                ]"
              >
                {{ excludedCount }}
              </span>
            </div>
          </div>
        </div>

        <!-- Sidebar Card 2: Search Criteria -->
        <div class="ard-card">
          <div class="ard-card-header">
            <span class="ard-card-title">Search criteria</span>
          </div>
          <div class="ard-card-body ard-criteria-sidebar">
            <div v-if="activeSearchTypes.length" class="ard-criteria-group">
              <div class="ard-criteria-label">Search type(s)</div>
              <div class="ard-criteria-value">
                {{ activeSearchTypes.join(", ") }}
              </div>
            </div>
            <div v-if="getSearchCodes" class="ard-criteria-group">
              <div class="ard-criteria-label">Codes</div>
              <div class="ard-criteria-value">{{ getSearchCodes }}</div>
            </div>
            <div v-if="woStatusLabel" class="ard-criteria-group">
              <div class="ard-criteria-label">Work Order Status</div>
              <div class="ard-criteria-value">{{ woStatusLabel }}</div>
            </div>
            <div v-if="woTypeLabel" class="ard-criteria-group">
              <div class="ard-criteria-label">Work Order Type</div>
              <div class="ard-criteria-value">{{ woTypeLabel }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GcWoBulkUpdateActivityLog from "./gc-wo-bulk-update-activity-log.vue";
// GcWoBulkUpdateStepPreview import removed while the "Change preview" card
// below is commented out — re-add both together when it's restored.

export default {
  name: "GcWoBulkUpdateAuditRecord",
  components: {
    GcWoBulkUpdateActivityLog,
  },
  props: {
    transactionId: { type: [String, Number], default: "" },
    description: { type: String, default: "" },
    status: { type: String, default: "D" },
    dateCreated: { type: String, default: null },
    createdBy: { type: String, default: "" },
    dateCommitted: { type: String, default: null },
    committedBy: { type: String, default: "" },
    wosUpdatedCount: { type: Number, default: 0 },
    wosIncludedCount: { type: Number, default: 0 },
    searchCriteria: { type: Object, default: () => ({}) },
    materialUpdateConfig: { type: Object, default: null },
    diffSnapshot: { type: Object, default: null },
    matchedWoList: { type: Array, default: () => [] },
    excludedWoIds: { type: Array, default: () => [] },
    woOutcomes: { type: Array, default: () => [] },
    activityLog: { type: Array, default: () => [] },
  },
  data() {
    return {
      activeOutcomeFilter: "all",
    };
  },
  computed: {
    statusLabel() {
      const labels = {
        C: "Committed",
        CE: "Committed with errors",
        X: "Cancelled",
        D: "Draft",
        P: "Processing",
      };
      return labels[this.status] || "Unknown";
    },
    statusClass() {
      // CE is a valid committed outcome (partial success), not a failure —
      // keep it visually distinct from a hard error badge.
      const classes = {
        C: "ard-badge-success",
        CE: "ard-badge-warning",
        X: "ard-badge-cancelled",
        D: "ard-badge-draft",
        P: "ard-badge-processing",
      };
      return classes[this.status] || "";
    },
    createdByFormatted() {
      if (!this.dateCreated || !this.createdBy) return "—";
      return `${this.createdBy} · ${this.formatTimestamp(this.dateCreated)}`;
    },
    committedByFormatted() {
      if (!this.dateCommitted || !this.committedBy) return "—";
      return `${this.committedBy} · ${this.formatTimestamp(
        this.dateCommitted,
      )}`;
    },
    materialCriteria() {
      const sc = this.searchCriteria || {};
      return sc.MaterialCriteria || sc.materialCriteria || [];
    },
    scopeFilters() {
      const sc = this.searchCriteria || {};
      return sc.ScopeFilters || sc.scopeFilters || {};
    },
    activeSearchTypes() {
      const seen = {};
      const labels = [];
      this.materialCriteria.forEach((crit) => {
        const label = this.formatSearchType(crit.SearchType);
        if (label && !seen[label]) {
          seen[label] = true;
          labels.push(label);
        }
      });
      return labels;
    },
    getSearchCodes() {
      const seen = {};
      const codes = [];
      this.materialCriteria.forEach((crit) => {
        (crit.MaterialCodes || []).forEach((code) => {
          if (!seen[code]) {
            seen[code] = true;
            codes.push(code);
          }
        });
      });
      return codes.length ? codes.join(", ") : null;
    },
    woStatusLabel() {
      const statuses = this.scopeFilters.WoStatuses || [];
      if (!statuses.length) return null;
      const map = {
        D: "Draft",
        P: "Planning",
        A: "Awaiting Production",
        I: "In Production",
        R: "Review",
        C: "Complete",
        X: "Cancelled",
      };
      return statuses
        .map((s) => map[(s || "").trim()] || s)
        .join(", ");
    },
    woTypeLabel() {
      return this.scopeFilters.WoTypeCode || null;
    },
    hasDiff() {
      return !!(
        this.diffSnapshot &&
        this.diffSnapshot.WoDiffs &&
        this.diffSnapshot.WoDiffs.length
      );
    },
    allOutcomeRows() {
      if (this.woOutcomes.length > 0) {
        return this.woOutcomes.map((outcome) => {
          // Tolerate both full-word ("Excluded"/"Failed") and single-letter
          // ("E"/"F") status codes — exact API enum values are unconfirmed.
          const inclusion = (outcome.InclusionStatus || "").trim();
          const outcomeStatus = (outcome.OutcomeStatus || "").trim();
          // Excluded via manual exclusion in Step 1/2 (InclusionStatus) OR
          // auto-excluded at Apply time due to WO status R/C/X (OutcomeStatus)
          // — the latter keeps InclusionStatus = "Included".
          const isExcluded =
            inclusion === "Excluded" ||
            inclusion === "E" ||
            outcomeStatus === "Excluded" ||
            outcomeStatus === "E";
          const isFailed = outcomeStatus === "Failed" || outcomeStatus === "F";
          return {
            wono: outcome.Wono,
            ProductDescription: outcome.ProductDescription || "—",
            status: isExcluded ? "excluded" : isFailed ? "failed" : "success",
            failureReason:
              outcome.FailureReason ||
              (isExcluded ? "Manually excluded during preview" : null),
          };
        });
      }
      return [];
    },
    successCount() {
      return this.allOutcomeRows.filter((r) => r.status === "success").length;
    },
    failedCount() {
      return this.allOutcomeRows.filter((r) => r.status === "failed").length;
    },
    excludedCount() {
      return this.allOutcomeRows.filter((r) => r.status === "excluded")
        .length;
    },
    filteredRows() {
      switch (this.activeOutcomeFilter) {
        case "success":
          return this.allOutcomeRows.filter((r) => r.status === "success");
        case "failed":
          return this.allOutcomeRows.filter((r) => r.status === "failed");
        case "excluded":
          return this.allOutcomeRows.filter((r) => r.status === "excluded");
        default:
          return this.allOutcomeRows;
      }
    },
    outcomeTabs() {
      return [
        { key: "all", label: "All", count: this.allOutcomeRows.length },
        { key: "success", label: "Success", count: this.successCount },
        { key: "failed", label: "Failed", count: this.failedCount },
        { key: "excluded", label: "Excluded", count: this.excludedCount },
      ];
    },
  },
  created() {
    if (this.failedCount > 0) {
      this.activeOutcomeFilter = "failed";
    }
  },
  methods: {
    formatTimestamp(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      return d.toLocaleString("en-AU", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    formatSearchType(searchType) {
      const typeMap = {
        CodeOnly: "Material Code(s)",
        QtyRangeOnly: "Material Qty Range",
        CodeAndQty: "Material Code(s) & Qty Range",
        MaterialAttributes: "Material Attributes",
      };
      return typeMap[searchType] || searchType;
    },
    formatOperation(operation) {
      const opMap = {
        UpdateQtyAll: "Update Qty — all matched materials",
        UpdateQtyNominated: "Update Qty — nominated material code(s)",
        ReplaceMaterial: "Replace material",
        ReplaceMaterialAndQty: "Replace material and update qty",
      };
      return opMap[operation] || operation;
    },
    outcomeBadgeClass(status) {
      const classes = {
        success: "ard-badge-success",
        failed: "ard-badge-errors",
        excluded: "ard-badge-cancelled",
      };
      return classes[status] || "";
    },
    outcomeLabel(status) {
      const labels = {
        success: "Success",
        failed: "Failed",
        excluded: "Excluded",
      };
      return labels[status] || "Unknown";
    },
  },
};
</script>

<style lang="scss">
.gc-wo-bulk-update-audit-record {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;

  .ard-layout {
    padding-top: 10px;
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .ard-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ard-sidebar {
    width: 280px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  // ── Card Structure ──────────────────────────────────────────────────────
  .ard-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 0;
  }

  .ard-card-header {
    padding: 12px 16px;
    border-bottom: 1px solid #eaecf0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ard-card-title {
    font-size: 14px;
    font-weight: 700;
    color: #101828;
  }

  .ard-card-body {
    padding: 16px;
  }

  .ard-card-body-compact {
    padding: 0;
  }

  .ard-prose-body {
    font-size: 13px;
    color: #475467;
    line-height: 1.8;
  }

  .ard-label {
    font-weight: 600;
    color: #344054;
  }

  .ard-separator {
    margin: 0 4px;
  }

  // ── Form Grid ──────────────────────────────────────────────────────────
  .ard-form-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .ard-form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .ard-field-label {
    font-size: 12px;
    font-weight: 500;
    color: #00000087;
    letter-spacing: 0.04em;
  }

  .ard-field-value {
    font-size: 12px;
    color: #101828;
  }

  .ard-value-primary {
    font-weight: 700;
    color: #35baf6;
  }

  .ard-mono {
    font-family: monospace;
    letter-spacing: 0.04em;
  }

  // ── Filter Tabs ────────────────────────────────────────────────────────
  .ard-card-filters {
    display: flex;
    gap: 8px;
    padding: 12px 16px 0 16px;
    border-bottom: 1px solid #eaecf0;
    background: #f8f9fa;
  }

  .ard-filter-tab {
    background: none;
    border: none;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #6c757d;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;

    &:hover {
      color: #495057;
    }

    &.is-active {
      color: #35baf6;
      border-bottom-color: #35baf6;
    }
  }

  .ard-tab-label {
    white-space: nowrap;
  }

  .ard-tab-count {
    font-size: 11px;
    color: inherit;
  }

  // ── Data Table ─────────────────────────────────────────────────────────
  .ard-dtable {
    display: flex;
    flex-direction: column;
    width: 100%;

    .failure-reason {
      > span {
        text-wrap: auto;
      }
    }
  }

  .ard-dtable-head {
    display: flex;
    background: #f8f9fa;
    border-bottom: 1px solid #eaecf0;
    border-top: 1px solid #eaecf0;
  }

  .ard-dtable-hcell {
    font-size: 12px;
    font-weight: 500;
    color: #475467;
    padding: 10px 16px;
    flex-shrink: 0;
    user-select: none;
  }

  .ard-dtable-row {
    display: flex;
    border-bottom: 1px solid #eaecf0;
    align-items: center;
    transition: background 0.1s;

    &:hover {
      background: #fafbfc;
    }

    &.ard-row-failed {
      background: #fff5f5;
    }

    .ard-reason-excluded {
      color: #98a2b3;
    }
  }

  .ard-dtable-cell {
    font-size: 12px;
    color: #475467;
    padding: 14px 16px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ard-col-product {
    flex: 1;
    min-width: 0;
    text-wrap: auto;
    min-width: 250px;
    max-width: 300px;
  }

  .ard-text-center {
    justify-content: center;
  }

  .ard-dtable-empty {
    padding: 20px 16px;
    text-align: center;
    font-size: 13px;
    color: #98a2b3;
  }

  .ard-dtable-foot {
    font-size: 12px;
    color: #98a2b3;
    padding: 10px 16px;
    border-top: 1px solid #eaecf0;
    background: #f8f9fa;
  }

  // ── Badges ──────────────────────────────────────────────────────────────
  .ard-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 400;
    white-space: nowrap;
  }

  .ard-badge-success {
    background: #d1fadf;
    color: #057a3d;
  }

  .ard-badge-errors {
    background: #fee4e2;
    color: #b42318;
  }

  .ard-badge-cancelled {
    background: #f8f9fa;
    color: #98a2b3;
  }

  .ard-badge-draft {
    background: #fef0c7;
    color: #b54708;
  }

  .ard-badge-warning {
    background: #fef0c7;
    color: #92400e;
  }

  .ard-badge-processing {
    background: #e0f2fe;
    color: #0369a1;
  }

  // ── Summary Sidebar ────────────────────────────────────────────────────
  .ard-sum-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #eaecf0;
    font-size: 13px;

    &:last-child {
      border-bottom: none;
    }
  }

  .ard-sum-label {
    color: #98a2b3;
    font-weight: 500;
  }

  .ard-sum-value {
    font-weight: 700;
    font-size: 14px;
    color: #101828;
  }

  .ard-sv-blue {
    color: #35baf6;
  }

  .ard-sv-red {
    color: #d92d20;
  }

  .ard-sv-green {
    color: #057a3d;
  }

  .ard-sv-muted {
    color: #adb5bd;
  }

  // ── Criteria Sidebar ───────────────────────────────────────────────────
  .ard-criteria-sidebar {
    font-size: 13px;
    color: #475467;
    line-height: 1.8;
  }

  .ard-criteria-group {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .ard-criteria-label {
    font-size: 11px;
    font-weight: 500;
    color: #98a2b3;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 4px;
  }

  .ard-criteria-value {
    font-size: 13px;
    color: #475467;
  }
}
</style>
